import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Building2, Plus, Search, Pencil, Trash2, Mail, Phone, MapPin, CheckCircle2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { confirmAsync } from '@/lib/confirm';

function Proveedores({ user }) {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [formData, setFormData] = useState({
    nombre_comercial: '',
    razon_social: '',
    nif: '',
    direccion: '',
    codigo_postal: '',
    poblacion: '',
    provincia: '',
    pais: 'España',
    email: '',
    telefono: '',
    categoria_gasto: 'servicios',
    cuenta_contable: '400000',
    forma_pago: 'transferencia',
    iban: '',
    observaciones: '',
    web_url: '',
    web_usuario: '',
    web_password: ''
  });
  const [saving, setSaving] = useState(false);
  const [showWebPassword, setShowWebPassword] = useState(false);

  const categorias = [
    { value: 'servicios', label: 'Servicios' },
    { value: 'suministros', label: 'Suministros' },
    { value: 'material', label: 'Material' },
    { value: 'otros', label: 'Otros' }
  ];

  const formasPago = [
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta', label: 'Tarjeta' },
    { value: 'cheque', label: 'Cheque' }
  ];

  useEffect(() => {
    loadProveedores();
  }, []);

  const loadProveedores = async () => {
    try {
      const response = await axiosInstance.get('/proveedores');
      setProveedores(response.data.proveedores || []);
    } catch (error) {
      console.error('Error cargando proveedores:', error);
      toast.error('Error al cargar los proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (proveedor = null) => {
    if (proveedor) {
      setEditingProveedor(proveedor);
      setFormData(proveedor);
    } else {
      setEditingProveedor(null);
      setFormData({
        nombre_comercial: '',
        razon_social: '',
        nif: '',
        direccion: '',
        codigo_postal: '',
        poblacion: '',
        provincia: '',
        pais: 'España',
        email: '',
        telefono: '',
        categoria_gasto: 'servicios',
        cuenta_contable: '400000',
        forma_pago: 'transferencia',
        iban: '',
        observaciones: '',
        web_url: '',
        web_usuario: '',
        web_password: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nombre_comercial || !formData.razon_social || !formData.nif) {
      toast.error('Nombre comercial, razón social y NIF son obligatorios');
      return;
    }

    setSaving(true);
    try {
      if (editingProveedor) {
        await axiosInstance.put(`/proveedores/${editingProveedor.id}`, formData);
        toast.success('Proveedor actualizado correctamente');
      } else {
        await axiosInstance.post('/proveedores', formData);
        toast.success('Proveedor creado correctamente');
      }
      setShowModal(false);
      loadProveedores();
    } catch (error) {
      console.error('Error guardando proveedor:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el proveedor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (proveedorId) => {
    if (!(await confirmAsync('¿Estás seguro de eliminar este proveedor?'))) return;

    try {
      await axiosInstance.delete(`/proveedores/${proveedorId}`);
      toast.success('Proveedor eliminado correctamente');
      loadProveedores();
    } catch (error) {
      console.error('Error eliminando proveedor:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el proveedor');
    }
  };

  const handleValidateNIF = async () => {
    if (!formData.nif) return;

    try {
      const response = await axiosInstance.post('/proveedores/validate-nif', {
        nif: formData.nif
      });

      if (response.data.valid) {
        if (response.data.exists) {
          toast.warning('Este NIF ya existe en otro proveedor');
        } else {
          toast.success('NIF válido');
        }
      } else {
        toast.error(response.data.message || 'NIF no válido');
      }
    } catch (error) {
      console.error('Error validando NIF:', error);
      toast.error('Error al validar el NIF');
    }
  };

  const handleAprobarProveedor = async (proveedorId) => {
    setApprovingId(proveedorId);
    try {
      await axiosInstance.post(`/proveedores/${proveedorId}/aprobar`);
      toast.success('Proveedor aprobado correctamente');
      loadProveedores();
    } catch (error) {
      console.error('Error aprobando proveedor:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar el proveedor');
    } finally {
      setApprovingId(null);
    }
  };

  const filteredProveedores = proveedores.filter(prov => {
    const matchesSearch = searchTerm === '' ||
      prov.nombre_comercial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prov.razon_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prov.nif?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategoria = filterCategoria === 'all' || prov.categoria_gasto === filterCategoria;
    const matchesEstado = filterEstado === 'all' || prov.estado === filterEstado;

    return matchesSearch && matchesCategoria && matchesEstado && prov.activo !== false;
  });

  const getCategoriaColor = (categoria) => {
    const colors = {
      servicios: 'bg-blue-100 text-blue-800',
      suministros: 'bg-green-100 text-green-800',
      material: 'bg-purple-100 text-purple-800',
      otros: 'bg-gray-100 text-gray-800'
    };
    return colors[categoria] || colors.otros;
  };

  const getEstadoColor = (estado) => {
    return estado === 'por_revisar' 
      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
      : 'bg-green-100 text-green-800';
  };

  const getEstadoLabel = (estado) => {
    return estado === 'por_revisar' ? 'Por Revisar' : 'Revisado';
  };

  const proveedoresPorRevisar = proveedores.filter(p => p.estado === 'por_revisar' && p.activo !== false).length;
  const proveedoresRevisados = proveedores.filter(p => p.estado === 'revisado' && p.activo !== false).length;

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando proveedores...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-indigo-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
                Proveedores
              </h1>
            </div>
            <Button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nuevo Proveedor
            </Button>
          </div>
          <p className="text-base text-gray-600">
            Gestión de proveedores para facturas recibidas y compras
          </p>
        </div>

        {/* Tabs de estado */}
        <Tabs value={filterEstado} onValueChange={setFilterEstado} className="mb-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all">
              Todos ({proveedores.filter(p => p.activo !== false).length})
            </TabsTrigger>
            <TabsTrigger value="revisado">
              Revisados ({proveedoresRevisados})
            </TabsTrigger>
            <TabsTrigger value="por_revisar">
              Por Revisar ({proveedoresPorRevisar})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filtros y búsqueda */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por nombre, razón social o NIF..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md"
                  value={filterCategoria}
                  onChange={(e) => setFilterCategoria(e.target.value)}
                >
                  <option value="all">Todas las categorías</option>
                  {categorias.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de proveedores */}
        <div className="text-sm text-gray-600 mb-4">
          Mostrando {filteredProveedores.length} de {proveedores.length} proveedores
        </div>

        {filteredProveedores.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No hay proveedores
              </h3>
              <p className="text-gray-500 text-center max-w-md mb-4">
                {searchTerm || filterCategoria !== 'all'
                  ? 'No se encontraron proveedores con los filtros aplicados'
                  : 'Comienza agregando tu primer proveedor'}
              </p>
              {!searchTerm && filterCategoria === 'all' && (
                <Button onClick={() => handleOpenModal()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Primer Proveedor
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProveedores.map((proveedor) => (
              <Card key={proveedor.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">
                        {proveedor.nombre_comercial}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {proveedor.razon_social}
                      </CardDescription>
                    </div>
                    <Badge className={getCategoriaColor(proveedor.categoria_gasto)}>
                      {categorias.find(c => c.value === proveedor.categoria_gasto)?.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getEstadoColor(proveedor.estado)}>
                      {getEstadoLabel(proveedor.estado)}
                    </Badge>
                    {proveedor.creado_por_ocr && (
                      <Badge variant="outline" className="text-xs">
                        Creado por OCR
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 className="h-3 w-3" />
                      <span className="font-mono">{proveedor.nif}</span>
                    </div>
                    {proveedor.email && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-3 w-3" />
                        <span className="truncate">{proveedor.email}</span>
                      </div>
                    )}
                    {proveedor.telefono && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="h-3 w-3" />
                        <span>{proveedor.telefono}</span>
                      </div>
                    )}
                    {proveedor.poblacion && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{proveedor.poblacion}, {proveedor.provincia}</span>
                      </div>
                    )}
                    <div className="pt-2 mt-2 border-t">
                      <div className="text-xs text-gray-500">
                        Cuenta: {proveedor.cuenta_contable}
                      </div>
                    </div>
                  </div>
                  
                  {/* Botón Acceder a la Web */}
                  {proveedor.web_url && (
                    <div className="mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                        onClick={() => window.open(proveedor.web_url, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Acceder a la Web
                      </Button>
                    </div>
                  )}
                  
                  {/* Botones de acción */}
                  <div className="flex gap-2 mt-4">
                    {proveedor.estado === 'por_revisar' ? (
                      <>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => handleAprobarProveedor(proveedor.id)}
                          disabled={approvingId === proveedor.id}
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {approvingId === proveedor.id ? 'Aprobando...' : 'Aprobar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenModal(proveedor)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(proveedor.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleOpenModal(proveedor)}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(proveedor.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal crear/editar */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProveedor ? 'Editar' : 'Nuevo'} Proveedor
              </DialogTitle>
              <DialogDescription>
                {editingProveedor ? 'Modifica' : 'Completa'} los datos del proveedor
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Datos identificativos */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nombre_comercial">Nombre Comercial *</Label>
                  <Input
                    id="nombre_comercial"
                    value={formData.nombre_comercial}
                    onChange={(e) => setFormData({ ...formData, nombre_comercial: e.target.value })}
                    placeholder="Empresa S.L."
                  />
                </div>
                <div>
                  <Label htmlFor="razon_social">Razón Social *</Label>
                  <Input
                    id="razon_social"
                    value={formData.razon_social}
                    onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                    placeholder="Empresa Servicios S.L."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nif">NIF/CIF *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="nif"
                      value={formData.nif}
                      onChange={(e) => setFormData({ ...formData, nif: e.target.value.toUpperCase() })}
                      placeholder="B12345678"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleValidateNIF}
                    >
                      Validar
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="+34 XXX XXX XXX"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@empresa.com"
                />
              </div>

              {/* Dirección */}
              <div>
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Calle, número"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="codigo_postal">Código Postal</Label>
                  <Input
                    id="codigo_postal"
                    value={formData.codigo_postal}
                    onChange={(e) => setFormData({ ...formData, codigo_postal: e.target.value })}
                    placeholder="28001"
                  />
                </div>
                <div>
                  <Label htmlFor="poblacion">Población</Label>
                  <Input
                    id="poblacion"
                    value={formData.poblacion}
                    onChange={(e) => setFormData({ ...formData, poblacion: e.target.value })}
                    placeholder="Madrid"
                  />
                </div>
                <div>
                  <Label htmlFor="provincia">Provincia</Label>
                  <Input
                    id="provincia"
                    value={formData.provincia}
                    onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                    placeholder="Madrid"
                  />
                </div>
              </div>

              {/* Clasificación contable */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="categoria_gasto">Categoría de Gasto</Label>
                  <select
                    id="categoria_gasto"
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.categoria_gasto}
                    onChange={(e) => setFormData({ ...formData, categoria_gasto: e.target.value })}
                  >
                    {categorias.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="cuenta_contable">Cuenta Contable</Label>
                  <Input
                    id="cuenta_contable"
                    value={formData.cuenta_contable}
                    onChange={(e) => setFormData({ ...formData, cuenta_contable: e.target.value })}
                    placeholder="400000"
                  />
                </div>
              </div>

              {/* Datos de pago */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="forma_pago">Forma de Pago</Label>
                  <select
                    id="forma_pago"
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.forma_pago}
                    onChange={(e) => setFormData({ ...formData, forma_pago: e.target.value })}
                  >
                    {formasPago.map(fp => (
                      <option key={fp.value} value={fp.value}>{fp.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="iban">IBAN</Label>
                  <Input
                    id="iban"
                    value={formData.iban}
                    onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
                    placeholder="ES00 0000 0000 0000 0000 0000"
                  />
                </div>
              </div>

              {/* Acceso Web del Proveedor */}
              <div className="border-t pt-6 mt-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Acceso Web del Proveedor</h3>
                  <p className="text-sm text-gray-600">
                    Información de acceso a la web del proveedor (portal de clientes, descargas, etc.)
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="web_url">Página Web</Label>
                    <Input
                      id="web_url"
                      type="url"
                      value={formData.web_url}
                      onChange={(e) => setFormData({ ...formData, web_url: e.target.value })}
                      placeholder="https://portal.proveedor.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="web_usuario">Usuario</Label>
                      <Input
                        id="web_usuario"
                        value={formData.web_usuario}
                        onChange={(e) => setFormData({ ...formData, web_usuario: e.target.value })}
                        placeholder="usuario@empresa.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="web_password">Contraseña</Label>
                      <div className="relative">
                        <Input
                          id="web_password"
                          type={showWebPassword ? "text" : "password"}
                          value={formData.web_password}
                          onChange={(e) => setFormData({ ...formData, web_password: e.target.value })}
                          placeholder="••••••••"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowWebPassword(!showWebPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showWebPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div className="border-t pt-6 mt-6">
                <Label htmlFor="observaciones">Observaciones</Label>
                <textarea
                  id="observaciones"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                  value={formData.observaciones}
                  onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default Proveedores;
